import { type ColumnDef, createColumnHelper } from '@tanstack/table-core';
import { FormattedMessage } from 'react-intl';

import { TableHeader } from '@/components/DataTable/TableHeader';
import type { K8SClusterStatus } from '@/lib/queries/integrations/containers/k8sClusters';

const columnHelper = createColumnHelper<K8SClusterStatus>();

type ClusterHealthStatus = 'healthy' | 'stale' | 'error';

const getHealthStatus = (
  cluster: K8SClusterStatus,
  currentTime: number,
): ClusterHealthStatus => {
  const now = new Date(currentTime);
  const twentyMinutesAgo = new Date(now.getTime() - 20 * 60 * 1000);
  const lastHeartbeat = new Date(cluster.last_heartbeat);

  if (lastHeartbeat > twentyMinutesAgo) {
    return 'healthy';
  }

  return 'stale';
};

const formatLastHeartbeat = (
  timestamp: string,
  currentTime: number,
): string => {
  const date = new Date(timestamp);
  const now = new Date(currentTime);
  const diffInMinutes = Math.floor(
    (now.getTime() - date.getTime()) / (1000 * 60),
  );

  if (diffInMinutes < 1) {
    return 'Just now';
  }
  if (diffInMinutes === 1) {
    return '1 minute ago';
  }
  if (diffInMinutes < 60) {
    return `${diffInMinutes} minutes ago`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours === 1) {
    return '1 hour ago';
  }
  if (diffInHours < 24) {
    return `${diffInHours} hours ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays === 1) {
    return '1 day ago';
  }

  return `${diffInDays} days ago`;
};

export const getColumns = (
  refreshTime: number = Date.now(),
): ColumnDef<K8SClusterStatus>[] =>
  [
    columnHelper.accessor('cluster_name', {
      header: ({ column }) => (
        <TableHeader
          column={column}
          headersTranslationPath="integrations.containers.kubernetes.clusters.table"
          type="sort-only"
        />
      ),
      size: 20,
    }),
    columnHelper.accessor('agent_version', {
      header: ({ column }) => (
        <TableHeader
          column={column}
          headersTranslationPath="integrations.containers.kubernetes.clusters.table"
          type="sort-only"
        />
      ),
      size: 20,
    }),
    columnHelper.accessor('last_heartbeat', {
      header: ({ column }) => (
        <TableHeader
          column={column}
          headersTranslationPath="integrations.containers.kubernetes.clusters.table"
          type="sort-only"
        />
      ),
      cell: ({ getValue }) => {
        const timestamp = getValue();

        return formatLastHeartbeat(timestamp, refreshTime);
      },
      size: 25,
    }),
    columnHelper.accessor('init', {
      header: ({ column }) => (
        <TableHeader
          column={column}
          headersTranslationPath="integrations.containers.kubernetes.clusters.table"
          type="sort-only"
        />
      ),
      cell: ({ getValue }) => {
        const isInitialized = getValue();

        return isInitialized ? '✅ Yes' : '⏳ Pending';
      },
      size: 20,
    }),
    columnHelper.accessor(cluster => getHealthStatus(cluster, refreshTime), {
      id: 'status',
      header: ({ column }) => (
        <TableHeader
          column={column}
          type="select"
          headersTranslationPath="integrations.containers.kubernetes.clusters.table"
          optionsTranslationPath="integrations.containers.kubernetes.clusters.table.status.options"
        />
      ),
      cell: ({ getValue }) => {
        const status = getValue();

        switch (status) {
          case 'healthy':
            return (
              <FormattedMessage id="integrations.containers.kubernetes.clusters.table.status.options.active" />
            );
          case 'stale':
            return (
              <FormattedMessage id="integrations.containers.kubernetes.clusters.table.status.options.stale" />
            );
          case 'error':
            return (
              <FormattedMessage id="integrations.containers.kubernetes.clusters.table.status.options.error" />
            );
          default:
            return (
              <FormattedMessage id="integrations.containers.kubernetes.clusters.table.status.options.error" />
            );
        }
      },
      size: 25,
    }),
  ] as ColumnDef<K8SClusterStatus>[];
